import React, { useEffect, useState } from 'react'
import { useI18n } from '../../i18n'
import { useConfigId } from '../../contexts/ConfigContext'
import { useToast } from '../../contexts/ToastContext'
import { getVariableDefinitions, updateVariableDefinitions } from '../../api/graph_settings'
import { getApiErrorMessage } from '../../api'
import {
  L2_SCHEMA,
  L3_SCHEMA,
  CORE_DERIVED_SCHEMA,
  MESSAGE_ANALYZER_OUTPUT_SCHEMA,
} from '../../config/graphEntities'
import { SettingsBlock } from '../../components/ui'
import VariableDefinitionsList from '../../components/VariableDefinitionsList'
import L3CalcText from '../../components/L3CalcText'
import '../../styles/pages/page-common.css'

const LEVEL_TABS = [
  { id: 'l1', labelKey: 'settings.variables.levelL1' },
  { id: 'l2', labelKey: 'settings.variables.levelL2' },
  { id: 'l3', labelKey: 'settings.variables.levelL3' },
  { id: 'l4', labelKey: 'settings.variables.levelL4' },
]

const L3_SUB_TABS = [
  { id: 'params', labelKey: 'settings.variables.l3.subTabParams' },
  { id: 'derived', labelKey: 'settings.variables.l3.derivedSection' },
]

const SettingsVariablesContent = React.forwardRef(function SettingsVariablesContent({ onBeforeSave, onDirtyChange, hidePanel }, ref) {
  const { t } = useI18n()
  const { showToast } = useToast()
  const configId = useConfigId()
  const [activeTab, setActiveTab] = useState('l4')
  const [l3SubTab, setL3SubTab] = useState('params')
  const [definitions, setDefinitions] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    getVariableDefinitions(configId)
      .then((r) => setDefinitions(r?.definitions ?? []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [configId])

  async function handleSave(next) {
    setSaving(true)
    setError(null)
    try {
      if (onBeforeSave) await onBeforeSave()
      await updateVariableDefinitions(next, configId)
      setDefinitions(next)
      showToast(t('toast.settings.saved'), 'success')
      if (onDirtyChange) onDirtyChange(false)
    } catch (e) {
      const msg = getApiErrorMessage(e, t('toast.settings.saveFailed'))
      showToast(msg, 'error')
    } finally {
      setSaving(false)
    }
  }

  const L3_KEYS = L3_SCHEMA.map((r) => r.key)

  if (loading) return <p className="settings-loading">{t('common.loading')}</p>
  if (error) return <p className="settings-error">{error}</p>

  return (
    <div className="settings-variables-tabs-layout">
      <nav className="settings-variables-tabs-row">
        {LEVEL_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`settings-tab ${activeTab === tab.id || (tab.id === 'l3' && activeTab === 'l3') ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {t(tab.labelKey) || tab.id.toUpperCase()}
          </button>
        ))}
      </nav>
      {activeTab === 'l3' && (
        <nav className="settings-variables-tabs-row settings-variables-l3-subtabs">
          {L3_SUB_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`settings-tab settings-tab--small ${l3SubTab === tab.id ? 'active' : ''}`}
              onClick={() => setL3SubTab(tab.id)}
            >
              {t(tab.labelKey) || tab.id}
            </button>
          ))}
        </nav>
      )}
      <div className="settings-variables-content">
      {activeTab === 'l1' && (
        <SettingsBlock
          label={t('settings.variables.l1Title')}
          description={t('settings.variables.l1Desc')}
        >
          <table className="settings-variables-table">
            <thead>
              <tr>
                <th>{t('settings.variables.key')}</th>
                <th>{t('settings.variables.type')}</th>
                <th>{t('settings.variables.range')}</th>
              </tr>
            </thead>
            <tbody>
              {MESSAGE_ANALYZER_OUTPUT_SCHEMA.map((row) => (
                <tr key={row.key} className="variable-level-l1">
                  <td><span className="variable-level-badge variable-level-badge--l1">L1</span> <code>{row.key}</code><br /><span className="settings-variables-key-desc">{t(row.descKey)}</span></td>
                  <td>{row.type}</td>
                  <td>{row.range || row.values || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </SettingsBlock>
      )}

      {activeTab === 'l2' && (
        <SettingsBlock
          label={t('settings.variables.l2Title')}
          description={t('settings.variables.l2Desc')}
        >
          <table className="settings-variables-table">
            <thead>
              <tr>
                <th>{t('settings.variables.key')}</th>
                <th>{t('settings.variables.type')}</th>
                <th>{t('settings.variables.range')}</th>
                <th>{t('settings.variables.purpose')}</th>
              </tr>
            </thead>
            <tbody>
              {L2_SCHEMA.map((row) => (
                <tr key={row.key} className="variable-level-l2">
                  <td><span className="variable-level-badge variable-level-badge--l2">L2</span> <code>{row.key}</code></td>
                  <td>{row.type}</td>
                  <td>{row.range}</td>
                  <td>{t(row.descKey)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </SettingsBlock>
      )}

      {activeTab === 'l3' && l3SubTab === 'params' && (
        <SettingsBlock
          label={t('settings.variables.l3Title')}
          description={t('settings.variables.l3DescHardcoded')}
        >
          <div className="variable-definitions-list">
            {L3_SCHEMA.map((row) => (
              <div key={row.key} className="variable-definition-block variable-definition-block--readonly variable-level-l3">
                <div className="variable-block-header">
                  <span className="variable-level-badge variable-level-badge--l3">L3</span>
                  <code className="variable-l3-key">{row.key}</code>
                  <span className="variable-block-type-badge">{row.type}</span>
                  <span className="variable-block-range">{row.range}</span>
                </div>
                <div className="variable-block-body">
                  <div className="variable-l3-purpose">
                    <strong>{t(row.purposeKey) || row.key}</strong>
                  </div>
                  <div className="variable-l3-calc">
                    <L3CalcText text={t(row.calcDescKey)} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </SettingsBlock>
      )}

      {activeTab === 'l3' && l3SubTab === 'derived' && (
        <SettingsBlock
          label={t('settings.variables.l3.derivedSection')}
          description={t('settings.variables.l3.derivedDesc')}
        >
          <div className="variable-definitions-list">
            {CORE_DERIVED_SCHEMA.map((row) => {
              const calcText = t(row.calcDescKey)
              return (
                <div key={row.key} className="variable-definition-block variable-definition-block--readonly variable-definition-block--derived variable-level-l3">
                  <div className="variable-block-header">
                    <span className="variable-level-badge variable-level-badge--l3">L3</span>
                    <code className="variable-l3-key">{row.key}</code>
                  </div>
                  <div className="variable-block-body">
                    <div className="variable-l3-purpose">
                      <strong>{t(row.purposeKey) || row.key}</strong>
                    </div>
                    <div className="variable-l3-calc">
                      <L3CalcText text={calcText} />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </SettingsBlock>
      )}

      {activeTab === 'l4' && (
        <SettingsBlock
          label={t('settings.variables.definitions')}
          description={t('settings.variables.definitionsDesc')}
        >
          <VariableDefinitionsList
            ref={ref}
            definitions={definitions}
            onSave={handleSave}
            saving={saving}
            level="l4"
            l3Keys={L3_KEYS}
            onDirtyChange={onDirtyChange}
            hidePanel={hidePanel}
          />
        </SettingsBlock>
      )}
      </div>
    </div>
  )
})

export default SettingsVariablesContent
